import { AnimatedCollapse } from '@backrs_ui/common-ui/src/atoms/AnimatedCollapse';
import { AppIcon, ICON_SIZES } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, SxProps, Theme, Typography, Tooltip } from '@mui/material';

import type { SidebarMenuItem } from './sidebar.types';

type SidebarItemProps = Readonly<{
  sidebarMenuItem: SidebarMenuItem;
  expandedItem?: string;
  setExpandedItem: (title?: string, shouldOpenSidebar?: boolean) => void;
  isSidebarOpen: boolean;
  setIsSideBarOpen?: (isOpen: boolean) => void;
}>;

export const SidebarItem = ({
  sidebarMenuItem,
  expandedItem,
  setExpandedItem,
  isSidebarOpen,
  setIsSideBarOpen,
}: SidebarItemProps) => {
  const { title, leftIcon, variant } = sidebarMenuItem;

  const isExpandable = variant.type === 'EXPANDABLE';
  const isExpanded = expandedItem === title && isSidebarOpen !== false;

  // if it's an expandable item, check if any of its sub items are active to determine if it should be active when the sidebar is collapsed
  const hasActiveSubItem =
    isExpandable &&
    variant.subItems.some(
      (subItem) => subItem.variant.type === 'NAVIGATION_ITEM' && subItem.variant.isActive,
    );

  const isActive = (!isExpandable && variant.isActive) || hasActiveSubItem || false;

  const isSubItem = (!isExpandable && variant.isSubItem) ?? false;

  const shouldShowTooltip = !isSidebarOpen && !isActive;

  const handleClick = () => {
    if (isExpandable) {
      setExpandedItem(isExpanded ? undefined : title, true);

      return;
    }

    if (!isSubItem) {
      setExpandedItem(undefined);
    }

    if (setIsSideBarOpen) {
      setIsSideBarOpen(false);
    }

    variant.onClick();
  };

  if (sidebarMenuItem.hidden) {
    return null;
  }

  return (
    <Box>
      <Tooltip title={shouldShowTooltip ? title : ''} placement="right" arrow enterDelay={100}>
        <Box
          sx={contentContainerSx(isActive || isExpanded, isSubItem, isSidebarOpen)}
          onClick={handleClick}
        >
          {/* APP ICON AND TITLE */}
          <Box sx={leftIconTitleContainerSx}>
            <Box sx={iconContainerSx}>
              {leftIcon && (
                <AppIcon
                  iconName={leftIcon.iconName}
                  diameter="20px"
                  color="Colors/Text/text-quaternary"
                  rotate={leftIcon?.rotate}
                />
              )}
            </Box>
            <AnimatedCollapse
              isOpen={isSidebarOpen !== undefined ? isSidebarOpen : true}
              direction="horizontal"
            >
              <Typography variant="text-md-semibold" sx={titleTextSx(isActive)}>
                {title}
              </Typography>
            </AnimatedCollapse>
          </Box>

          {/* CHEVRON ICON */}
          {isExpandable && (
            <AnimatedCollapse isOpen={isSidebarOpen} opacityFade>
              <Box sx={chevronContainerSx(isExpanded)}>
                <AppIcon iconName="ChevronUp" diameter="20px" color="Colors/Text/text-quaternary" />
              </Box>
            </AnimatedCollapse>
          )}
        </Box>
      </Tooltip>

      {/* SUB ITEMS */}
      {isExpandable && isSidebarOpen && (
        <AnimatedCollapse isOpen={isExpanded}>
          <Box sx={subItemsContainerSx}>
            {variant.subItems.map((subItem, index) => (
              <SidebarItem
                key={index}
                sidebarMenuItem={subItem}
                expandedItem={expandedItem}
                setExpandedItem={setExpandedItem}
                isSidebarOpen={isSidebarOpen}
                setIsSideBarOpen={setIsSideBarOpen}
              />
            ))}
          </Box>
        </AnimatedCollapse>
      )}
    </Box>
  );
};

const contentContainerSx =
  (isActive: boolean, isSubItem: boolean, isSideBarOpen: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    borderRadius: backrs.radius['radius-sm'],
    px: backrs.spacing['spacing-lg'],
    py: backrs.spacing['spacing-md'],
    border: '1px solid transparent',
    minWidth: backrs.spacing['spacing-5xl'],
    minHeight: backrs.spacing['spacing-5xl'],
    transition: 'all 0.3s ease',
    maxWidth: !isSideBarOpen ? '48px' : '100%',
    ...(isActive
      ? {
          backgroundColor: backrs.colors['Colors/Background/bg-primary'],
          color: backrs.colors['Colors/Text/text-brand-primary-hover'],
          border: `1px solid ${backrs.colors['Colors/Text/text-primary']}`,
        }
      : {}),
    '&:hover': {
      backgroundColor: backrs.colors['Colors/Background/bg-primary'],
      transform: isSubItem ? undefined : 'translate(-2px, -2px)',
      boxShadow: isSubItem ? 'none' : '4px 4px 0px 0px rgba(0, 0, 0, 1)',
      color: backrs.colors['Colors/Text/text-brand-primary-hover'],
      border: `1px solid ${backrs.colors['Colors/Text/text-primary']}`,
    },
    '&:active': {
      transform: 'translate(0px, 0px)',
      boxShadow: 'none',
      transition: 'all 0s',
    },
  });

const leftIconTitleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const titleTextSx =
  (isActive: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    color: isActive
      ? backrs.colors['Colors/Text/text-primary']
      : backrs.colors['Colors/Text/text-secondary'],
    userSelect: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      color: backrs.colors['Colors/Text/text-brand-primary-hover'],
    },
  });

const chevronContainerSx = (isExpanded: boolean): SxProps<Theme> => ({
  transition: `transform ${isExpanded ? '0.2s ease-in-out' : '0.2s ease'}`,
  transform: isExpanded ? 'rotate(-180deg)' : 'rotate(0deg)',
  height: ICON_SIZES['20px'],
  display: 'flex',
  alignItems: 'center',
});

const iconContainerSx: SxProps<Theme> = ({ backrs }) => ({
  py: backrs.spacing['spacing-xs'],
  boxSizing: 'border-box',
  width: ICON_SIZES['20px'],
  height: ICON_SIZES['20px'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const subItemsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  mt: backrs.spacing['spacing-sm'],
  pb: backrs.spacing['spacing-md'],
});
